import { useRef, useState } from "react";
import { X, Camera, Loader2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";

const MAX_DIMENSION = 320;   // px — resize avatar before storage
const JPEG_QUALITY = 0.72;
const MAX_BASE64_BYTES = 160_000; // matches backend cap (~120 KB base64)

/** Compress + resize the picked file to a small JPEG data URL, then return it as a base64 string. */
async function fileToCompressedDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        // Try JPEG at declining quality until it fits under the cap.
        let quality = JPEG_QUALITY;
        let dataUrl = canvas.toDataURL("image/jpeg", quality);
        while (dataUrl.length > MAX_BASE64_BYTES && quality > 0.35) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL("image/jpeg", quality);
        }
        if (dataUrl.length > MAX_BASE64_BYTES) return reject(new Error("Image too large — pick a smaller photo"));
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error("That doesn't look like an image"));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function StudentProfileEditModal({ initial, googlePicture, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    phone: initial?.phone || "",
    notes: initial?.notes || "",
    avatar: initial?.avatar || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const pickFile = () => fileRef.current?.click();
  const onFilePicked = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { setError("Please pick an image file"); return; }
    setUploading(true); setError("");
    try {
      const dataUrl = await fileToCompressedDataURL(f);
      setForm(prev => ({ ...prev, avatar: dataUrl }));
    } catch (err) { setError(err.message || "Upload failed"); }
    setUploading(false);
    e.target.value = ""; // reset for re-picks
  };

  const useGooglePhoto = () => setForm(prev => ({ ...prev, avatar: "" }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const body = {
        name: form.name || null,
        phone: form.phone || null,
        notes: form.notes || null,
        avatar: form.avatar || null,
      };
      const { data } = await api.patch("/student/profile", body);
      onSaved(data);
    } catch (err) { setError(formatApiError(err, "Save failed")); }
    setSaving(false);
  };

  const preview = form.avatar || googlePicture;

  return (
    <div style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", display:"grid", placeItems:"center", zIndex:60, padding:16}} onClick={onClose}>
      <form onSubmit={submit} onClick={e=>e.stopPropagation()} className="card p-6 w-[440px] max-w-[92vw] max-h-[92vh] overflow-y-auto" data-testid="edit-profile-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="label" style={{color:"var(--green)"}}>Edit profile</div>
            <div className="text-[20px] font-bold uppercase mt-1 tracking-tight">Update your info</div>
          </div>
          <button type="button" onClick={onClose} data-testid="edit-profile-close"><X size={18}/></button>
        </div>

        {/* Avatar */}
        <div className="flex items-center gap-4 mb-5">
          <button type="button" onClick={pickFile} className="relative w-20 h-20 rounded-full overflow-hidden shrink-0" style={{ border: "2px solid var(--border)" }} data-testid="avatar-picker">
            {preview
              ? <img src={preview} alt="" className="w-full h-full object-cover"/>
              : <div className="w-full h-full flex items-center justify-center" style={{ background: "var(--bg-elev)" }}><Camera size={22} style={{color:"var(--text-dim)"}}/></div>}
            <div className="absolute bottom-0 inset-x-0 flex items-center justify-center py-1" style={{ background: "rgba(0,0,0,0.55)" }}>
              {uploading ? <Loader2 size={12} className="animate-spin" color="#fff"/> : <Camera size={12} color="#fff"/>}
            </div>
          </button>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[13px] uppercase tracking-tight">Photo</div>
            <div className="label-tiny mt-1">Tap to upload a new photo</div>
            {form.avatar && googlePicture && (
              <button type="button" onClick={useGooglePhoto} className="label-tiny underline mt-2" style={{color:"var(--text-dim)"}} data-testid="use-google-photo">
                Use my Google photo instead
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFilePicked} data-testid="avatar-file-input"/>
        </div>

        <div className="space-y-3">
          <label className="block label">Name
            <input className="input mt-1" required value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="edit-name"/>
          </label>
          <label className="block label">Phone
            <input className="input mt-1" type="tel" placeholder="+507-6000-0000" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} data-testid="edit-phone"/>
          </label>
          <label className="block label">Emergency contact / bio <span className="label-tiny normal-case" style={{color:"var(--text-dim)"}}>(injuries, allergies, contact)</span>
            <textarea className="input mt-1" rows="3" value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} data-testid="edit-notes"/>
          </label>
        </div>

        {error && (
          <div className="label-tiny mt-3 flex items-start gap-2" style={{color:"var(--danger)"}} data-testid="edit-profile-error">
            <AlertCircle size={14} className="shrink-0 mt-0.5"/><span>{error}</span>
          </div>
        )}
        <button type="submit" disabled={saving || uploading} className="btn btn-primary w-full mt-5" data-testid="edit-profile-submit">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </form>
    </div>
  );
}
